import { CSSProperties, useEffect, useRef } from 'react'
import { PlaceData, getPlaceData } from '../common/googlePlacesAutoCompleteModel'

type Props = {
  id: string
  value: string
  placeholder?: string
  disabled: boolean
  style: CSSProperties
  onChange: (value: string) => void
  onBlur: () => void
  onPlaceSelected: (place: PlaceData) => void
}

/**
 * A text input for a street address that attaches Google Places Autocomplete
 * when it's available. The Places library is loaded app-wide in index.html, so
 * no API key is threaded here; if it hasn't loaded, this degrades to a plain
 * input the user can type into. Selecting a suggestion reports the parsed
 * address to `onPlaceSelected` (the caller fills the street/city/state/zip
 * fields); manual typing still flows through `onChange`.
 */
const FormAddressInput = ({
  id,
  value,
  placeholder,
  disabled,
  style,
  onChange,
  onBlur,
  onPlaceSelected,
}: Props) => {
  const inputRef = useRef<HTMLInputElement>(null)
  // Keep the latest callback without re-attaching the autocomplete listener.
  const onPlaceSelectedRef = useRef(onPlaceSelected)
  onPlaceSelectedRef.current = onPlaceSelected

  useEffect(() => {
    const places = window.google?.maps?.places
    if (!inputRef.current || !places) {
      return
    }

    const autocomplete = new places.Autocomplete(inputRef.current, {
      fields: ['address_components', 'geometry'],
      types: ['address'],
    })
    const listener = autocomplete.addListener('place_changed', () => {
      const place = autocomplete.getPlace()
      if (place?.address_components) {
        onPlaceSelectedRef.current(
          getPlaceData(place.address_components as never, place.geometry as never)
        )
      }
    })

    return () => {
      listener.remove()
      window.google?.maps?.event?.clearInstanceListeners(autocomplete)
    }
  }, [])

  return (
    <input
      id={id}
      ref={inputRef}
      type="text"
      value={value}
      placeholder={placeholder}
      disabled={disabled}
      autoComplete="off"
      onChange={(e) => onChange(e.target.value)}
      onBlur={onBlur}
      style={style}
    />
  )
}

export default FormAddressInput
